import { useEffect, useState, useCallback, useRef } from "react";
import { QrCode, Barcode, Printer, Zap, Search, X, Info, Camera, ScanLine } from "lucide-react";
import api from "../../lib/api";
import { errText } from "../../lib/errors";
import { toast } from "sonner";
import { downloadApi } from "../../lib/download";
import { fmtQty4 } from "../../lib/format";

const tl = (n) => (n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const inputCls = "h-11 px-4 rounded-xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest/30 w-full";

export default function AdminBarcodes() {
  const [settings, setSettings] = useState(null);
  const [next, setNext] = useState(null);
  const [products, setProducts] = useState([]);
  const [prefix, setPrefix] = useState("");
  const [query, setQuery] = useState("");
  const [found, setFound] = useState(null);
  const [labelFor, setLabelFor] = useState(null);
  const [labelOpts, setLabelOpts] = useState({ qty: 12, size: "kucuk" });
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [camOpen, setCamOpen] = useState(false);
  const [scannerOn, setScannerOn] = useState(false);
  const scanBuf = useRef({ keys: "", last: 0 });

  useEffect(() => {
    if (!scannerOn) return;
    const onKey = (e) => {
      if (e.target.closest && e.target.closest("input, textarea, select")) return; // form alanlarına yazıyorsa karışma
      const b = scanBuf.current;
      const now = Date.now();
      if (now - b.last > 100) b.keys = "";
      b.last = now;
      if (e.key === "Enter") {
        const code = b.keys;
        b.keys = "";
        if (code.length >= 6) { setQuery(code); lookup(code); }
        return;
      }
      if (e.key.length === 1) b.keys += e.key;
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannerOn]);

  useEffect(() => {
    if (!camOpen) return;
    let scanner;
    let stopped = false;
    (async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        scanner = new Html5Qrcode("bcenter-reader");
        await scanner.start({ facingMode: "environment" }, { fps: 8, qrbox: { width: 220, height: 140 } },
          (decoded) => { stopped = true; scanner.stop().catch(() => {}); setCamOpen(false); setQuery(decoded); lookup(decoded); },
          () => {});
      } catch { toast.error("Kamera açılamadı (izin ve HTTPS gerekli)"); setCamOpen(false); }
    })();
    return () => { if (scanner && !stopped) scanner.stop().catch(() => {}); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camOpen]);

  const load = useCallback(async () => {
    try {
      const [{ data: s }, { data: n }, { data: p }] = await Promise.all([
        api.get("/admin/barcode/settings"),
        api.get("/admin/barcode/next"),
        api.get("/admin/products"),
      ]);
      setSettings(s);
      setPrefix(s.prefix);
      setNext(n);
      setProducts(p.items || []);
    } catch { toast.error("Barkod verileri yüklenemedi"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePrefix = async () => {
    setBusy(true);
    try {
      await api.put("/admin/barcode/settings", { prefix });
      toast.success("GS1 firma öneki kaydedildi");
      load();
    } catch (e) { toast.error(errText(e) || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  const generate = async (p) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/products/${p.product_id}/barcode/generate`);
      toast.success(`${p.name} → ${data.barcode}`);
      load();
    } catch (e) { toast.error(errText(e) || "Üretilemedi"); }
    finally { setBusy(false); }
  };

  const assignMissing = async () => {
    if (!window.confirm("Barkodu olmayan tüm aktif ürünlere EAN-13 otomatik atansın mı?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/barcode/assign-missing");
      toast.success(`${data.assigned} ürüne barkod atandı`);
      load();
    } catch (e) { toast.error(errText(e) || "Atama başarısız"); }
    finally { setBusy(false); }
  };

  const lookup = async (raw) => {
    const code = (raw ?? query).trim();
    if (!code) return;
    setFound(null);
    try {
      const { data } = await api.get(`/admin/stock/lookup/${encodeURIComponent(code)}`);
      setFound(data);
    } catch { toast.error("Bu barkoda ait ürün bulunamadı"); }
  };

  const downloadLabel = async () => {
    setBusy(true);
    try {
      await downloadApi(`/admin/products/${labelFor.product_id}/label.pdf`, `etiket-${labelFor.sku}.pdf`, labelOpts);
      toast.success("Etiket PDF'i indirildi");
      setLabelFor(null);
    } catch (e) { toast.error(errText(e) || "Etiket oluşturulamadı"); }
    finally { setBusy(false); }
  };

  const missing = products.filter((p) => !p.barcode).length;
  const filtered = products.filter((p) => {
    const s = q.toLowerCase();
    return !s || p.name.toLowerCase().includes(s) || (p.sku || "").toLowerCase().includes(s) || (p.barcode || "").includes(s);
  });

  return (
    <div data-testid="admin-barcodes-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-forest">Barkod Merkezi</h1>
          <p className="text-stone-500 text-sm mt-1">EAN-13/GTIN barkod üretimi, sorgulama ve etiket yazdırma — barkodlu stok ve satış sistemiyle tam entegre.</p>
        </div>
        <button data-testid="barcode-assign-missing" onClick={assignMissing} disabled={busy || !missing} className="flex items-center gap-2 bg-gold hover:bg-gold-dark disabled:bg-stone-300 text-stone-900 text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors">
          <Zap className="w-4 h-4" /> Eksik Barkodları Ata ({missing})
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm">
          <h3 className="font-semibold text-stone-900 text-sm flex items-center gap-2"><Info className="w-4 h-4 text-forest" /> GS1 Firma Öneki</h3>
          <div className="flex gap-2 mt-3">
            <input data-testid="barcode-prefix-input" className={inputCls} value={prefix} onChange={(e) => setPrefix(e.target.value)} placeholder="8690001" maxLength={7} />
            <button data-testid="barcode-prefix-save" onClick={savePrefix} disabled={busy} className="bg-forest hover:bg-forest-dark text-white text-xs font-semibold px-4 rounded-xl transition-colors shrink-0">Kaydet</button>
          </div>
          {next && <p className="text-xs text-stone-500 mt-2">Sıradaki barkod: <span className="font-mono font-semibold text-stone-800" data-testid="barcode-next-preview">{next.next_preview}</span></p>}
          <p className="text-[11px] text-stone-400 mt-2">Bu önekten {settings?.used_count ?? 0} barkod kullanımda.</p>
        </div>
        <div className="lg:col-span-2 bg-amber-50 border border-amber-200 rounded-2xl p-5">
          <h3 className="font-semibold text-amber-900 text-sm">GS1 Türkiye — Resmi barkod numarası nasıl alınır?</h3>
          <ol className="mt-2 text-xs text-amber-900 space-y-1 list-decimal list-inside">
            <li>gs1tr.org üzerinden GS1 Türkiye'ye üye olun ve firma önekinizi (GS1 Company Prefix) alın.</li>
            <li>Öneki soldaki alana girin — sistem bu önekle geçerli EAN-13 (kontrol haneli) üretir.</li>
            <li>Pazaryerleri (Trendyol, Hepsiburada...) resmi GS1 kodu ister; dahili önek yalnızca kendi kanallarınızda geçerlidir.</li>
          </ol>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm">
        <h3 className="font-semibold text-stone-900 text-sm mb-3">Barkod Sorgulama</h3>
        <div className="flex gap-2 max-w-lg">
          <div className="relative flex-1">
            <Barcode className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
            <input data-testid="barcode-query-input" className={`${inputCls} pl-10`} placeholder="Barkod okutun veya yazın..." value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && lookup()} />
          </div>
          <button data-testid="barcode-query-button" onClick={() => lookup()} className="flex items-center gap-1.5 bg-forest hover:bg-forest-dark text-white text-xs font-semibold px-4 rounded-xl transition-colors"><Search className="w-4 h-4" /> Sorgula</button>
          <button data-testid="barcode-query-camera" onClick={() => setCamOpen(true)} className="flex items-center gap-1.5 bg-stone-800 hover:bg-stone-900 text-white text-xs font-semibold px-4 rounded-xl transition-colors shrink-0" title="Kamera ile tara"><Camera className="w-4 h-4" /> Kamera</button>
          <button data-testid="barcode-scanner-toggle" onClick={() => setScannerOn((s) => !s)} className={`flex items-center gap-1.5 text-xs font-semibold px-4 rounded-xl transition-colors shrink-0 ${scannerOn ? "bg-emerald-600 text-white" : "bg-white border border-stone-300 text-stone-700 hover:border-forest"}`} title="USB barkod okuyucu modu"><ScanLine className="w-4 h-4" /> {scannerOn ? "Okuyucu Açık" : "Okuyucu"}</button>
        </div>
        {scannerOn && <p data-testid="barcode-scanner-hint" className="text-xs text-emerald-700 mt-2">Okuyucu modu aktif — USB barkod okuyucuyla sayfanın herhangi bir yerinde okutabilirsiniz; barkod otomatik sorgulanır.</p>}
        {found && (
          <div data-testid="barcode-query-result" className="mt-3 flex flex-wrap items-center gap-4 bg-forest/5 border border-forest/20 rounded-xl p-4">
            <QrCode className="w-8 h-8 text-forest" />
            <div className="flex-1 min-w-[180px]">
              <p className="font-semibold text-stone-900">{found.name}</p>
              <p className="text-xs text-stone-500 font-mono">{found.barcode} • SKU: {found.sku}</p>
            </div>
            <p className="text-sm font-bold text-stone-800">{tl(found.price)} ₺ • Stok: {fmtQty4(found.stock, found.base_unit || found.unit || "")}</p>
            <button data-testid="barcode-query-clear" onClick={() => { setFound(null); setQuery(""); }} className="text-xs text-stone-400 hover:text-stone-600 underline">Temizle</button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-stone-200/80 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-stone-100">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
            <input data-testid="barcode-product-search" className={`${inputCls} pl-10`} placeholder="Ürün, SKU veya barkod ara..." value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead><tr className="text-left text-xs uppercase font-bold tracking-wider text-stone-600 bg-[#EDE8DE] border-b border-stone-300">
              <th className="px-5 py-3">Ürün</th><th className="px-5 py-3">SKU</th><th className="px-5 py-3">Barkod (EAN-13)</th><th className="px-5 py-3 text-right">İşlem</th>
            </tr></thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.product_id} data-testid={`barcode-row-${p.product_id}`} className="border-b border-stone-50">
                  <td className="px-5 py-3 font-medium text-stone-900">{p.name}</td>
                  <td className="px-5 py-3 font-mono text-xs text-stone-500">{p.sku}</td>
                  <td className="px-5 py-3">{p.barcode ? <span className="font-mono text-sm font-semibold tracking-wider">{p.barcode}</span> : <span className="text-xs text-red-500 font-semibold">Barkod yok</span>}</td>
                  <td className="px-5 py-3 text-right">
                    {p.barcode
                      ? <button data-testid={`barcode-label-${p.product_id}`} onClick={() => { setLabelFor(p); setLabelOpts({ qty: 12, size: "kucuk" }); }} className="inline-flex items-center gap-1.5 text-xs font-semibold text-forest hover:bg-forest/5 px-3 py-1.5 rounded-lg transition-colors"><Printer className="w-3.5 h-3.5" /> Etiket</button>
                      : <button data-testid={`barcode-generate-${p.product_id}`} onClick={() => generate(p)} disabled={busy} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-gold/20 text-gold-dark hover:bg-gold/30 px-3 py-1.5 rounded-lg transition-colors"><Zap className="w-3.5 h-3.5" /> Barkod Üret</button>}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan="4" className="px-5 py-8 text-center text-stone-400">Ürün bulunamadı</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {labelFor && (
        <div data-testid="label-modal" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setLabelFor(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-forest">Etiket Yazdır</h3>
              <button data-testid="label-modal-close" onClick={() => setLabelFor(null)} className="p-1.5 hover:bg-stone-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm font-medium text-stone-800">{labelFor.name}</p>
            <p className="text-xs text-stone-400 font-mono mt-0.5">{labelFor.barcode}</p>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div><label className="text-xs font-semibold text-stone-600">Adet</label><input data-testid="label-qty" type="number" min="1" max="200" className={inputCls} value={labelOpts.qty} onChange={(e) => setLabelOpts({ ...labelOpts, qty: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-stone-600">Boyut</label>
                <select data-testid="label-size" className={inputCls} value={labelOpts.size} onChange={(e) => setLabelOpts({ ...labelOpts, size: e.target.value })}>
                  <option value="kucuk">Küçük (49×28mm, raf)</option>
                  <option value="buyuk">Büyük (98×55mm, koli)</option>
                </select></div>
            </div>
            <button data-testid="label-download" onClick={downloadLabel} disabled={busy} className="mt-5 w-full bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white font-semibold py-3 rounded-xl transition-colors">{busy ? "Hazırlanıyor..." : "PDF Etiket İndir"}</button>
          </div>
        </div>
      )}

      {camOpen && (
        <div data-testid="barcode-camera-modal" className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setCamOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-stone-900">Kamera ile Barkod Tara</h3>
              <button data-testid="barcode-camera-close" onClick={() => setCamOpen(false)} className="p-1.5 hover:bg-stone-100 rounded-lg text-stone-500">Kapat</button>
            </div>
            <div id="bcenter-reader" className="w-full rounded-xl overflow-hidden bg-stone-900 min-h-[220px]" />
            <p className="text-xs text-stone-400 mt-2">Telefon/tablet kamerasını barkoda yaklaştırın; okununca otomatik sorgulanır.</p>
          </div>
        </div>
      )}
    </div>
  );
}
